// Programa de validación (§12): los Riesgos 1 a 4 de todas las cuentas contra su umbral, en un solo lugar. Es lo
// que decide un cambio de fase, y antes del MVP son ensayos sobre negocios de prueba (Decisión 11).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Cargando, PageHead, Tabla, TarjetaIndicador, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { momento } from '../lib/formato';
import { useSesion } from '../lib/sesion';

export function Riesgos() {
  const [dias, setDias] = useState(28);
  const q = useQuery({ queryKey: ['panel-riesgos', dias], queryFn: () => api<any>(`/api/panel/riesgos?dias=${dias}`) });
  const evaluaciones = useQuery({ queryKey: ['panel-evaluaciones'], queryFn: () => api<any[]>('/api/panel/evaluaciones') });
  if (q.isLoading || !q.data) return <Cargando />;

  return (
    <div className="page">
      <PageHead title="Programa de validación" subtitle="Los Riesgos 1 a 4 contra su umbral. Antes del MVP son ensayos sobre negocios de prueba: prueban que el producto mide bien, no cierran ningún riesgo." />
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[14, 28, 56].map((d) => <button key={d} className={`chip ${d === dias ? 'tabla-row-selected' : ''}`} onClick={() => setDias(d)}>{d} días</button>)}
      </div>
      <div className="kpis">
        {q.data.medidas.map((m: any) => (
          <TarjetaIndicador
            key={m.riesgo}
            label={`Riesgo ${m.riesgo} · ${m.titulo}`}
            value={m.valor === null ? '—' : `${m.valor}${m.unidad === '%' ? '%' : ''}`}
            unit={m.unidad === '%' ? undefined : m.unidad}
            tone={m.cumple === null ? 'neutral' : m.cumple ? 'ok' : 'error'}
            trend={`${m.detalle} · umbral ${m.umbral}${m.unidad === '%' ? '%' : ` ${m.unidad}`}`}
          />
        ))}
      </div>
      <div className="eyebrow" style={{ marginTop: 18 }}>Riesgo 1 · días con el dato completo, por negocio</div>
      <Tabla rowKey="cuentaId" rows={q.data.cobertura}
        columns={[
          { key: 'negocio', label: 'Negocio' },
          { key: 'soluciones', label: 'Soluciones', numeric: true },
          { key: 'pct', label: 'Cobertura', numeric: true, render: (c: any) => (c.pct === null ? '—' : <Badge state={c.pct >= 85 ? 'ok' : c.pct >= 50 ? 'pending' : 'error'}>{`${c.pct}%`}</Badge>) },
        ]} />

      <div className="eyebrow" style={{ marginTop: 18 }}>Riesgo 4 · registros que llegan por MCP</div>
      <Tabla rowKey="cuentaId" rows={q.data.vias}
        columns={[
          { key: 'negocio', label: 'Negocio' },
          { key: 'total', label: 'Registros', numeric: true },
          { key: 'mcp', label: 'Por MCP', numeric: true },
          { key: 'pctMcp', label: 'Parte', numeric: true, render: (v: any) => (v.pctMcp === null ? '—' : <Badge state={v.pctMcp >= 40 ? 'ok' : 'pending'}>{`${v.pctMcp}%`}</Badge>) },
        ]} />

      <div className="eyebrow" style={{ marginTop: 18 }}>Riesgo 2 · el contador y la suite de evaluación</div>
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 14 }}>
          {q.data.verificaciones.coincide + q.data.verificaciones.difiere} cálculos verificados por un contador ·{' '}
          <strong>{q.data.verificaciones.difiere}</strong> dieron otro valor · {q.data.verificaciones.enviada} sin responder
        </div>
      </div>
      {!evaluaciones.data?.length ? (
        <Vacio titulo="La suite de evaluación no ha corrido">Corre en cada pull request cuando hay clave de la API de OpenAI en los secretos.</Vacio>
      ) : (
        <Tabla rowKey="id" rows={evaluaciones.data}
          columns={[
            { key: 'corrida_en', label: 'Cuándo', render: (e: any) => momento(e.corrida_en) },
            { key: 'modelo', label: 'Modelo' },
            { key: 'guion', label: 'Guion', render: (e: any) => <code style={{ fontSize: 12 }}>{e.guion}{e.commit ? ` · ${e.commit}` : ''}</code> },
            { key: 'pct', label: 'Resueltos', numeric: true, render: (e: any) => <Badge state={e.pct >= e.umbral ? 'ok' : 'error'}>{`${e.pct}%`}</Badge> },
            { key: 'errores_de_calculo', label: 'Errores de cálculo', numeric: true, render: (e: any) => <Badge state={e.errores_de_calculo ? 'error' : 'ok'}>{e.errores_de_calculo}</Badge> },
            { key: 'sinteticos', label: 'Problemas', render: (e: any) => (e.sinteticos ? 'Sintéticos' : 'Reales') },
          ]} />
      )}
    </div>
  );
}
